import type { ProteinCalcProduct } from '@bulknutrients/bulk-shared-lib/interfaces/ProteinCalculator';
import { SkeletonImage, SkeletonText } from '@components/Placeholders/Skeleton';
import { useWindowSize } from '@hooks/useWindowSize';
import Link from 'next/link';
import { type ReactElement, useEffect, useState } from 'react';
import { ComparisonSelect } from './ComparisonSelect';
import { Disclaimer } from './Disclaimer';
import { Product } from './Product';
import { ProductProperties } from './ProductProperties';
import { ProductSelect } from './ProductSelect';

const DEFAULT_PRODUCTS = ['BN-WPC', 'BN-WPI', 'BN-PMP'];

/**
 * ProductSelect is to swap in Bulk products for the comparison
 * ComparisonSelect is to choose a competitor product
 * ProductProperties show protein, cost per serve etc
 */

export function ProteinCalculator({
  products,
  competitorBrands,
}: {
  products: ProteinCalcProduct[];
  competitorBrands: { brand: string; products: ProteinCalcProduct[] }[];
}): ReactElement {
  const { width } = useWindowSize();
  const [maxShownProducts, setMaxShownProducts] = useState(3);
  const [purest, setPurest] = useState(''); // most protein
  const [comparisonProduct, setComparisonProduct] =
    useState<ProteinCalcProduct>();
  const [shown, setShown] = useState(
    DEFAULT_PRODUCTS.slice(0, maxShownProducts)
  ); // the ones we are showing comparison to
  const [shownProducts, setShownProducts] = useState<ProteinCalcProduct[]>([]); // the ones we are showing comparison to
  const [swapIndex, setSwapIndex] = useState(0);

  // On resize change how many shown
  useEffect(() => {
    let mounted = true;
    if (mounted && width) {
      if (width > 768) {
        setMaxShownProducts(3);
      } else if (width > 640) {
        setMaxShownProducts(2);
      } else {
        setMaxShownProducts(1);
      }
    }

    return function cleanup() {
      mounted = false;
    };
  }, [width]);

  // When max shown changes, update the shown list
  useEffect(() => {
    const tmpSet = new Set([...shown, ...DEFAULT_PRODUCTS]);
    const tmpArr = Array.from(tmpSet);
    setShown(tmpArr.slice(0, maxShownProducts));
  }, [maxShownProducts]);

  // When shown products are changed, work out purest
  useEffect(() => {
    const mostPure = [...shownProducts].sort((a, b) =>
      a.protein > b.protein ? -1 : 1
    );
    if (mostPure[0]) setPurest(mostPure[0].protein);
  }, [shownProducts]);

  // When products are set, or swapped, select the ones we can show
  useEffect(() => {
    const arr = [] as ProteinCalcProduct[];
    for (const sku of shown) {
      const product = products.filter((p) => p.sku === sku)[0];
      if (product) {
        arr.push(product);
      }
    }
    setShownProducts(arr);
  }, [products, shown]);

  function removeProduct(sku: string) {
    const index = shown.indexOf(sku);
    if (index >= 0) {
      setSwapIndex(index);
      const updated = [...shown];
      updated.splice(index, 1);
      setShown(updated);
    }
  }

  function selectProduct(sku: string) {
    const updated = [...shown];
    updated.splice(swapIndex, 0, sku);
    setShown(updated);
  }

  return (
    <div
      data-testid="protein-calculator"
      className="relative isolate min-h-[50vh] w-full max-w-full 
    font-primary text-base text-grey-darkest dark:text-white md:p-[3vmin]"
    >
      <div
        className="relative m-0 mb-6 flex flex-wrap rounded bg-white 
      dark:bg-grey-darkest md:shadow-[rgba(0,0,0,0.1)_0_1vmin_3vmin]"
      >
        {shownProducts.length < maxShownProducts && (
          <ProductSelect products={products} select={selectProduct} />
        )}
        <div className="grid grid-cols-[1fr_3fr]">
          <ComparisonSelect
            brands={competitorBrands}
            setComparisonProduct={setComparisonProduct}
          />
          {shownProducts.length >= maxShownProducts && (
            <div
              className="grid grid-cols-1 overflow-hidden rounded bg-white shadow-[rgba(0,0,0,0.1)_0_10px_30px] 
            dark:bg-grey-darkest sm:grid-cols-2 md:grid-cols-3"
            >
              {shownProducts.map((product) => (
                <Product
                  product={product}
                  key={product.sku}
                  remove={removeProduct}
                  comparisonProduct={comparisonProduct}
                  purest={purest}
                />
              ))}
            </div>
          )}
          {comparisonProduct && (
            <div>
              <ProductProperties product={comparisonProduct} purest={purest} />
              {/* extra div is so bottom dashed border aligns with bn compared products that have additioinal shop now row  */}
              <div />
            </div>
          )}
          {!comparisonProduct && <div />}
          <div className="grid grid-cols-1 overflow-hidden rounded-sm bg-white shadow-[rgba(0,0,0,0.1)_0_10px_30px] dark:bg-grey-dark sm:grid-cols-2 md:grid-cols-3">
            {shownProducts.map((product) => (
              <div key={product.sku}>
                <ProductProperties
                  product={product}
                  purest={purest}
                  comparisonProduct={comparisonProduct}
                />
                <div className="bg-white p-[2vmin] text-center dark:bg-grey-darkest">
                  <Link
                    prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
                    href={product.slug}
                    target="_blank"
                    itemProp="url"
                    className="inline-block rounded border-none bg-orange px-6 py-2
                    font-bold uppercase tracking-wider text-white no-underline shadow-[inset_rgba(0,0,0,0.15)_0_-2px_0,rgba(0,0,0,0.1)_0_2px_5px]
                    duration-200
                    [text-shadow:rgba(0,0,0,0.25)_0_1px_0] hover:scale-105"
                  >
                    Shop now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Disclaimer />
    </div>
  );
}

export function ProteinCalculatorSkeleton(): ReactElement {
  return (
    <div
      className="relative isolate min-h-[50vh] w-full max-w-full 
    font-primary text-base text-grey-darkest dark:text-white md:p-[3vmin]"
    >
      <div className="grid grid-cols-[1fr_3fr] gap-4">
        <div className="flex flex-col gap-4">
          <SkeletonImage widthPixels={165} />
          <SkeletonText heightPixels={190} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          {[...Array(3)].map((_, idx) => (
            <div key={`skeleton-product-${idx}`}>
              <SkeletonImage widthPixels={165} />
              <SkeletonText heightPixels={600} />
            </div>
          ))}
        </div>
      </div>
      <Disclaimer />
    </div>
  );
}
